<script setup>
import { computed, onMounted, reactive, ref, watch, getCurrentInstance } from 'vue'
import { router } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useCart } from '@/composables/useCart';
import Button from 'primevue/button';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

defineOptions({
    layout: MainLayout,
});

const { formatConvertedPrice, getCurrentCurrency, onCurrencyChange } = useCurrencyFormatter();
const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();
const { loadCart } = useCart();

// Mock coinStore para evitar erro do Pinia
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const currentCurrency = ref(getCurrentCurrency());

const isGamblingSite = (site) => {
    if (!site.offers || !Array.isArray(site.offers)) {
        return false;
    }
    return site.offers.some(offer => 
        offer.type && offer.type.name && 
        (offer.type.name.toLowerCase() === 'casinos' || offer.type.name.toLowerCase() === 'bet')
    );
};

const isCryptoSite = (site) => {
    if (!site.offers || !Array.isArray(site.offers)) {
        return false;
    }
    return site.offers.some(offer => 
        offer.type && offer.type.name && offer.type.name.toLowerCase() === 'crypto'
    );
};

const isSponsorSite = (site) => {
    return site.link_type === 'SPONSOR' || 
           site.link_type === 'NOFOLLOW_AND_SPONSOR' || 
           site.link_type === 'DOFOLLOW_AND_SPONSOR' || 
           site.link_type === 'NOFOLLOW_DOFOLLOW_SPONSOR';
};

const props = defineProps({
    title: String,
    sites: Object,
    coins: Object,
    favorites: Array,
    interests: Array,
    filters: Object,
    project: Object,
    projects: Array,
});

const links = computed(() => {
    const _links = props.sites.links
    _links.shift()
    _links.pop()
    return _links
})

const _defaultColumns = [
    { key: 'sale', label: 'Price', visible: true },
    { key: 'url', label: 'Domain', visible: true },
    { key: 'da', label: 'DA', visible: true },
    { key: 'dr', label: 'DR', visible: true },
    { key: 'projects_in', label: 'Projects in', visible: true },
    { key: 'gambling', label: 'Accepts gambling', visible: true },
    { key: 'sponsor', label: 'Marked as sponsored', visible: true },
    { key: 'cripto', label: 'Cripto', visible: false },
    { key: 'ssl', label: 'SSL', visible: false },
    { key: 'category', label: 'Category', visible: false },
    { key: 'obs', label: 'Obs', visible: true },
    { key: 'example', label: 'Example', visible: false },
    { key: 'inserted_at', label: 'Upload date', visible: false },
];

const _columns =
    localStorage.getItem('client.sites.index.columns') ?
        unionBy(JSON.parse(localStorage.getItem('client.sites.index.columns')), _defaultColumns, 'key')
        : _defaultColumns

const columns = ref(_columns)

watch(columns, (n, o) => {
    localStorage.setItem('client.sites.index.columns', JSON.stringify(columns.value))
}, {
    deep: true,
})

const sort = ref(props.filters.sort)

// Process sites with highest sale like in Sites.vue
const sitesWithHighestSale = computed(() => {
    const _sites = JSON.parse(JSON.stringify(props.sites.data))

    _sites.forEach(site => {
        // Backend provides highest_sale in cents — keep in cents for convertPrice
        site.highest_sale = site.highest_sale || 0
        site.highest_sale_coin_ratio = site.highest_sale_coin ? coinStore.ratios[site.highest_sale_coin] : 1
    })

    return _sites
})

const filters = reactive({
    favorites: props.filters.filter.favorites,
    interests: props.filters.filter.interests,
    project: props.filters.filter.project,
})

watch(sort, (n, o) => get());

const get = async () => {
    router.get(proxy.route ? proxy.route('client.sites.list') : '/sites-list', {
        sort: sort.value,
        filter: {
            ...(filters.favorites && {
                favorites: filters.favorites,
            }),
            ...(filters.interests && {
                interests: filters.interests,
            }),
            ...(filters.project && {
                project: filters.project,
            }),
        },
    }, {
        preserveState: true,
    })
}

const toggleFavorite = async (site) => {
    router.post(proxy.route ? proxy.route('client.sites.favorite', site) : `/sites/${site}/favorite`, null, {
        preserveScroll: true,
    })
}

const toggleInterest = async (site) => {
    router.post(proxy.route ? proxy.route('client.sites.interest', site) : `/sites/${site}/interest`, null, {
        preserveScroll: true,
    })
}

// Project dialog state
const showProjectDialog = ref(false);
const selectedSiteForProject = ref(null);
const selectedProjectId = ref(null);

const addToProject = (siteId) => {
    selectedSiteForProject.value = siteId;
    selectedProjectId.value = null;
    showProjectDialog.value = true;
}

const isSiteInProject = (projectId, siteId) => {
    const site = sitesWithHighestSale.value.find(s => s.id === siteId);
    if (!site || !site.project_ids) return false;

    const projectIds = site.project_ids.toString().split(',').map(id => parseInt(id));
    return projectIds.includes(projectId);
}

// Helper function to get project names for a site
const getSiteProjects = (siteId) => {
    const site = sitesWithHighestSale.value.find(s => s.id === siteId);
    if (!site || !site.project_ids) return [];

    const projectIds = site.project_ids.toString().split(',').map(id => parseInt(id));
    return props.projects.filter(p => projectIds.includes(p.id));
}

// Function to remove site from project
const removeFromProject = async (siteId, projectId) => {
    try {
        const routeName = proxy.route ?
            proxy.route('client.sites.project', { site: siteId, project: projectId }) :
            `/sites/${siteId}/project/${projectId}`;

        await router.post(routeName, {}, {
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Site removed from project successfully'),
                    life: 3000
                });
            },
            onError: () => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: $t('Failed to remove site from project'),
                    life: 3000
                });
            }
        });
    } catch (e) {
        console.error('Error removing from project:', e);
    }
}

const confirmAddToProject = async () => {
    if (!selectedProjectId.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a project'),
            life: 3000
        });
        return;
    }

    try {
        const routeName = proxy.route ?
            proxy.route('client.sites.project', { site: selectedSiteForProject.value, project: selectedProjectId.value }) :
            `/sites/${selectedSiteForProject.value}/project/${selectedProjectId.value}`;

        await router.post(routeName, {}, {
            onSuccess: () => {
                showProjectDialog.value = false;
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Site added to project successfully'),
                    life: 3000
                });
            },
            onError: () => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: $t('Failed to add site to project'),
                    life: 3000
                });
            }
        });
    } catch (e) {
        console.error('Error adding to project:', e);
    }
}
// const currentSymbol = ref(getCurrentCurrency());
onMounted(async () => {
    try {
        await loadCart();
    } catch (error) {
        console.error('Error loading cart on mount:', error);
    }

    setTimeout(() => {
        if (window.tippy) {
            window.tippy('[data-tippy-content]');
        }
    }, 100)

    onCurrencyChange(({ currency, symbol }) => {
        currentCurrency.value = currency;
    });

    currentCurrency.value = getCurrentCurrency();
})

const content = computed(() => {
    if(filters.favorites) {
        return {
            title: $t('Your favorites'),
            subtitle: $t('The best of the best'),
            stepone: $t('Your account'),
            steptwo: $t('Favorites'),
        }
    }

    if(filters.interests) {
        return {
            title: $t('Your interests'),
            subtitle: $t('Websites that you saved to take a look later'),
            stepone: $t('Your account'),
            steptwo: $t('Interest list'),
        }
    }

    if(filters.hasOwnProperty('project')) {
        return {
            title: props.project.name,
            subtitle: $t('Project collection'),
            stepone: $t('Projects'),
            steptwo: props.project.name,
        }
    }

    return {
        title: '',
        subtitle: '',
        stepone: '',
        steptwo: '',
    }
})

// Helper function to resolve unionBy since it's not available in Vue 3
function unionBy(arr1, arr2, key) {
    const result = [...arr1];
    const keys = new Set(arr1.map(item => item[key]));

    for (const item of arr2) {
        if (!keys.has(item[key])) {
            result.push(item);
            keys.add(item[key]);
        }
    }

    return result;
}
</script>

<template>
    <Toast />

    <!-- Project Selection Dialog -->
    <Dialog
        v-model:visible="showProjectDialog"
        :header="$t('Add to Project')"
        :modal="true"
        :style="{ width: '450px' }"
    >
        <div class="flex flex-col gap-4">
            <p class="text-gray-600">{{ $t('Select a project to add this site to:') }}</p>

            <div v-if="props.projects && props.projects.length > 0" class="flex flex-col gap-3">
                <div v-for="project in props.projects" :key="project.id"
                    class="flex items-center gap-3 p-3 border border-gray-200 rounded-lg relative"
                    :class="{
                        'bg-blue-50 border-blue-300': selectedProjectId === project.id,
                        'opacity-60 cursor-not-allowed': isSiteInProject(project.id, selectedSiteForProject),
                        'hover:bg-gray-50 cursor-pointer': !isSiteInProject(project.id, selectedSiteForProject)
                    }"
                    @click="!isSiteInProject(project.id, selectedSiteForProject) && (selectedProjectId = project.id)"
                >
                    <RadioButton
                        :modelValue="selectedProjectId"
                        :value="project.id"
                        :inputId="`project-${project.id}`"
                        :disabled="isSiteInProject(project.id, selectedSiteForProject)"
                    />
                    <div
                        class="w-10 h-10 rounded flex items-center justify-center text-white font-semibold text-sm"
                        :style="{ backgroundColor: project.color }"
                    >
                        {{ project.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) }}
                    </div>
                    <label :for="`project-${project.id}`" class="flex-1" :class="{ 'cursor-pointer': !isSiteInProject(project.id, selectedSiteForProject), 'cursor-not-allowed': isSiteInProject(project.id, selectedSiteForProject) }">
                        <div class="font-semibold text-gray-900 flex items-center gap-2">
                            {{ project.name }}
                            <span v-if="isSiteInProject(project.id, selectedSiteForProject)"
                                class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                <i class="pi pi-check text-xs mr-1"></i>
                                {{ $t('Already added') }}
                            </span>
                        </div>
                        <div class="text-sm text-gray-600">
                            {{ project.sites_count ?? 0 }} {{ (project.sites_count ?? 0) === 1 ? $t('website') : $t('websites') }}
                        </div>
                    </label>
                </div>
            </div>

            <div v-else class="text-center py-8">
                <i class="pi pi-folder-open text-4xl text-gray-300 mb-3"></i>
                <p class="text-gray-600 mb-3">{{ $t('No projects found') }}</p>
                <Button
                    :label="$t('Create Project')"
                    icon="pi pi-plus"
                    @click="() => { showProjectDialog = false; router.get(proxy.route ? proxy.route('client.projects.create') : '/projects/create'); }"
                    severity="primary"
                    size="small"
                />
            </div>
        </div>

        <template #footer>
            <Button
                :label="$t('Cancel')"
                text
                @click="showProjectDialog = false"
            />
            <Button
                :label="$t('Add to Project')"
                @click="confirmAddToProject"
                :disabled="!selectedProjectId"
                severity="primary"
            />
        </template>
    </Dialog>

<!--     <div class="rounded-md bg-white px-5 py-4 shadow sm:px-6">
        <div class="flex flex-col">
            <div>
                <h2 class="text-2xl font-bold text-gray-900">{{ content.title }}</h2>
               <span class="text-sm text-gray-400">{{ content.subtitle }}</span>
            </div>
        </div>
    </div> -->

    <div class="mt-5 hidden sm:block">
        <div class="flex flex-col">

            <div>
                <h2 class="font-semibold text-xl text-gray-800 leading-tight">{{
                    $t('Showing results:')
                }}&nbsp;{{ sites.total }}&nbsp;{{ $t('sites found') }}</h2>
            </div>

            <hr class="my-5">

<!--            <div class="flex flex-col">-->
<!--                <Accordion :expandIcon="'pi pi-plus'" :collapseIcon="'pi pi-minus'">-->
<!--                    <AccordionTab :header="$t('Column filter:')">-->
<!--                        <div class="flex flex-wrap">-->
<!--                            <div v-for="(column, index) in columns" :key="index" class="px-4 py-2 relative flex">-->
<!--                                <div class="flex items-center h-5">-->
<!--                                    <input v-model="column.visible" :value="column.key" :id="column.key"-->
<!--                                        :name="column.key" type="checkbox"-->
<!--                                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded" />-->
<!--                                </div>-->

<!--                                <div class="ml-2 text-sm">-->
<!--                                    <label :for="column.key" class="font-medium text-gray-700 whitespace-nowrap">{{-->
<!--                                        $t(column.label)-->
<!--                                    }}</label>-->
<!--                                </div>-->
<!--                            </div>-->
<!--                        </div>-->
<!--                    </AccordionTab>-->
<!--                </Accordion>-->
<!--            </div>-->

            <div
                class="mt-5 min-w-full overflow-hidden overflow-x-auto align-middle border border-gray-200 sm:rounded-md">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th v-show="columns[0].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Price') }}</div>
                            </th>

                            <th v-show="columns[1].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Domain') }}</div>
                            </th>
                            <th v-show="columns[2].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('DA') }}</div>
                            </th>

                            <th v-show="columns[3].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('DR') }}</div>
                            </th>
                            <th v-show="columns[4].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Projects in') }}</div>
                            </th>
                            <th v-show="columns[5].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Accepts gambling') }}</div>
                            </th>
                            <th v-show="columns[6].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Marked as sponsored') }}</div>
                            </th>
                            <th v-show="columns[7].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Cripto') }}</div>
                            </th>
                            <th v-show="columns[8].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('SSL') }}</div>
                            </th>
                            <th v-show="columns[9].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Category') }}</div>
                            </th>
                            <th v-show="columns[10].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Obs') }}</div>
                            </th>
                            <th v-show="columns[11].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Example') }}</div>
                            </th>
                            <th v-show="columns[12].visible"
                                class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Upload date') }}</div>
                            </th>
                            <th class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Add to favorites') }}</div>
                            </th>
<!--                            <th class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"-->
<!--                                scope="col">{{ $t('Add to interest list') }}</th>-->
                            <th class="bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-900"
                                scope="col">
                                <div class="h-6 flex items-center">{{ $t('Add to a project') }}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                        <tr v-for="(site, index) in sitesWithHighestSale" :key="index" class="bg-white">
                            <td v-show="columns[0].visible" class="whitespace-nowrap px-4 py-4 text-sm">
                                <div class="flex items-center gap-2">
                                    <span>
                                        {{ formatConvertedPrice(site.converted_sale || site.highest_sale, site.converted_sale_coin || site.highest_sale_coin, site.highest_sale, site.highest_sale_coin) }}
                                    </span>
                                </div>
                            </td>
                            <td v-show="columns[1].visible" class="whitespace-nowrap px-4 py-4 text-sm">
                                <a :href="proxy.route ? proxy.route('client.sites.show', site.id) : `/sites/${site.id}`"
                                    class="text-gray-500 hover:text-gray-900">
                                {{ site.real_url }}
                                </a>
                            </td>
                            <td v-show="columns[2].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ site.da ?? '-' }}
                            </td>
                            <td v-show="columns[3].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ site.dr ?? '-' }}
                            </td>
                            <td v-show="columns[4].visible" class="px-4 py-4 text-sm text-gray-500">
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        v-for="project in getSiteProjects(site.id)"
                                        :key="project.id"
                                        class="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full group"
                                        :style="{ backgroundColor: project.color + '20', color: project.color }"
                                    >
                                        {{ project.name }}
                                        <button
                                            @click.stop="removeFromProject(site.id, project.id)"
                                            class="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors"
                                            :title="$t('Remove from project')"
                                        >
                                            <i class="pi pi-times text-xs font-bold"></i>
                                        </button>
                                    </span>
                                    <span v-if="getSiteProjects(site.id).length === 0" class="text-gray-400">-</span>
                                </div>
                            </td>
                            <td v-show="columns[5].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ $t(isGamblingSite(site) ? 'Yes' : 'No') }}
                            </td>
                            <td v-show="columns[6].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ $t(isSponsorSite(site) ? 'Yes' : 'No') }}
                            </td>
                            <td v-show="columns[7].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ $t(isCryptoSite(site) ? 'Yes' : 'No') }}
                            </td>
                            <td v-show="columns[9].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                <span :data-tippy-content="site.category?.subtitle">{{ site.category?.title ??
                                    '-' }}</span>
                            </td>
                            <td v-show="columns[10].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ site.obs ?? '-' }}
                            </td>
                            <td v-show="columns[11].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                -
                            </td>
                            <td v-show="columns[12].visible"
                                class="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                                {{ site.formatted_inserted_at }}
                            </td>
                            <td class="px-4 py-4 text-sm text-gray-500">
                                <button @click="toggleFavorite(site.id)">
                                    <i v-if="favorites.includes(site.id)"
                                       class="pi pi-heart-fill text-red-500 text-lg"
                                    />
                                    <i v-else
                                       class="pi pi-heart text-gray-400 text-lg"
                                    />
                                </button>
                            </td>

<!--                            <td class="px-4 py-4 text-sm text-gray-500">-->
<!--                                <button @click="toggleInterest(site.id)">-->
<!--                                    <i v-if="interests.includes(site.id)"-->
<!--                                       class="pi pi-flag-fill text-blue-500 text-lg"-->
<!--                                    />-->
<!--                                    <i v-else-->
<!--                                       class="pi pi-flag text-gray-400 text-lg"-->
<!--                                    />-->
<!--                                </button>-->
<!--                            </td>-->

                            <td class="px-4 py-4 text-sm text-gray-500">
                                <Button
                                    icon="pi pi-plus-circle"
                                    severity="success"
                                    text
                                    rounded
                                    size="small"
                                    @click="addToProject(site.id)"
                                    :title="$t('Add to Project')"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <nav class="mt-6 flex items-center justify-between border-t border-gray-200 px-4 sm:px-0">
                <div class="-mt-px flex w-0 flex-1">
                    <a :href="sites.prev_page_url"
                        class="inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700">
                    <i class="pi pi-arrow-left mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                    {{ $t('Previous') }}
                    </a>
                </div>
                <div class="hidden md:-mt-px md:flex">
                    <a v-for="(link, index) in links" :key="index" :href="link.url"
                        :class="[link.active ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700', 'inline-flex items-center border-t-2  px-4 pt-4 text-sm font-medium ']">
                    {{ link.label }}
                    </a>
                </div>
                <div class="-mt-px flex w-0 flex-1 justify-end">
                    <a :href="sites.next_page_url"
                        class="inline-flex items-center border-t-2 border-transparent pt-4 pl-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700">
                    {{ $t('Next') }}
                <i class="pi pi-arrow-right ml-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                </a>
            </div>
        </nav>
    </div>
</div>
</template>
